import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmDialog } from '../../context/ConfirmContext';
import styles from '../../styles/modules/admin/AdminAvailability.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface Availability {
  _id: string;
  userId: {
    _id: string;
    username: string;
    fullName: string;
    email: string;
    userType: string;
    profilePicture?: string;
  };
  date: string;
  startTime: string;
  endTime: string;
  place: string;
  needsTransport: boolean;
  status: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const AdminAvailability: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { confirmDelete } = useConfirmDialog();
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAvailability, setSelectedAvailability] = useState<Availability | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchAvailabilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, dateFilter, searchTerm]);

  const fetchAvailabilities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFilter && { date: dateFilter }),
      });

      const response = await fetch(
        getApiUrl(`api/admin/availability?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAvailabilities(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching availabilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvailability = async (availabilityId: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete availability?',
      message: 'Are you sure you want to delete this availability?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel'
    });
    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        getApiUrl(`api/admin/availability/${availabilityId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showToast({ message: t('admin.availability.deleteSuccess'), type: 'success' });
        fetchAvailabilities();
      } else {
        const errorData = await response.json();
        showToast({ message: `${t('admin.availability.deleteFailed')}: ${errorData.message || t('messages.errorOccurred')}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting availability:', error);
      showToast({ message: t('admin.availability.deleteFailed'), type: 'error' });
    }
  };

  const viewAvailabilityDetails = (availability: Availability) => {
    setSelectedAvailability(availability);
    setShowModal(true);
  };

  const getStatusBadgeClass = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    const statusMap: Record<string, string> = {
      available: adminCommonStyles.statusAvailable,
      booked: adminCommonStyles.statusBooked,
      cancelled: adminCommonStyles.statusCancelled,
    };

    return [adminCommonStyles.statusBadge, statusMap[normalizedStatus] || adminCommonStyles.statusInactive]
      .filter(Boolean)
      .join(' ');
  };

  const formatTime = (time?: string) => {
    if (!time) return 'N/A';
    return time.substring(0, 5);
  };

  if (loading && availabilities.length === 0) {
    return (
      <div className={styles.adminAvailability}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.adminAvailability}>
      <div className={styles.listPageHeader}>
        <button
          className={headerStyles.backButton}
          onClick={() => navigate('/admin')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>{t('admin.availability.title')}</h1>
        <div></div>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.controlsSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder={t('admin.availability.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filters}>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.filterDate}
            />
            
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.filterSelect}
            >
              <option value="all">{t('admin.availability.allStatuses')}</option>
              <option value="available">{t('admin.availability.statusAvailable')}</option>
              <option value="booked">{t('admin.availability.statusBooked')}</option>
              <option value="cancelled">{t('admin.availability.statusCancelled')}</option>
            </select>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.availabilityTable}>
            <thead>
              <tr>
                <th>{t('admin.availability.staffName')}</th>
                <th>{t('admin.availability.email')}</th>
                <th>{t('admin.availability.date')}</th>
                <th>{t('admin.availability.time')}</th>
                <th>{t('admin.availability.location')}</th>
                <th>Transport</th>
                <th>{t('admin.availability.status')}</th>
                <th>{t('admin.availability.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {availabilities.map((availability) => (
                <tr key={availability._id}>
                  <td>
                    <strong>{availability.userId.fullName}</strong>
                  </td>
                  <td>{availability.userId.email}</td>
                  <td>{new Date(availability.date).toLocaleDateString(t('common.locale') || 'en-US')}</td>
                  <td>{formatTime(availability.startTime)} - {formatTime(availability.endTime)}</td>
                  <td>{availability.place}</td>
                  <td>
                    <span
                      className={`${styles.transportBadge} ${availability.needsTransport ? styles.transportNeeded : styles.transportHas}`}
                    >
                      {availability.needsTransport ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                          Needed
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Has
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(availability.status)}>
                      {availability.status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        className={adminCommonStyles.btnView}
                        onClick={() => viewAvailabilityDetails(availability)}
                        title={t('admin.availability.viewDetails')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      <button
                        className={adminCommonStyles.btnDelete}
                        onClick={() => handleDeleteAvailability(availability._id)}
                        title={t('admin.availability.delete')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {availabilities.length === 0 && (
          <div className={styles.noResults}>
            {t('admin.availability.noResults')}
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
               {t('common.previous')}
            </button>
            <span className={styles.paginationInfo}>
              {t('common.pageOf', { current: currentPage, total: totalPages })}
            </span>
            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.next')} 
            </button>
          </div>
        )}
      </div>

      {showModal && selectedAvailability && (
        <div className={adminCommonStyles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{t('admin.availability.availabilityDetails')}</h2>
              <button className={adminCommonStyles.modalClose} onClick={() => setShowModal(false)}>
                
              </button>
            </div>
            <div className={adminCommonStyles.modalBody}>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.staffName')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedAvailability.userId.fullName}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.staffEmail')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedAvailability.userId.email}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Username:</span>
                <span className={adminCommonStyles.detailValue}>{selectedAvailability.userId.username}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.date')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  {new Date(selectedAvailability.date).toLocaleDateString(t('common.locale') || 'en-US')}
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.time')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  {formatTime(selectedAvailability.startTime)} - {formatTime(selectedAvailability.endTime)}
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.location')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedAvailability.place}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Transport:</span>
                <span className={adminCommonStyles.detailValue}>
                  {selectedAvailability.needsTransport ? 'Needs transport' : 'Has transport'}
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.status')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  <span className={getStatusBadgeClass(selectedAvailability.status)}>
                    {selectedAvailability.status.toUpperCase()}
                  </span>
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.availability.created')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  {new Date(selectedAvailability.createdAt).toLocaleString()}
                </span>
              </div>
              {selectedAvailability.notes && (
                <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                  <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Notes:</span>
                  <span className={adminCommonStyles.detailValue}>{selectedAvailability.notes}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAvailability;
